import { SegmentedControl, Center } from "@mantine/core";
import { CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";

type BooleanToggleTheme = "check" | "eye";

export interface BooleanToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  /** Icon theme: "check" (default) = CheckCircle/XCircle, "eye" = Eye/EyeOff */
  theme?: BooleanToggleTheme;
  /** Custom on/off icons — overrides theme */
  onIcon?: LucideIcon;
  offIcon?: LucideIcon;
  /** On/off labels (shown next to icons) */
  onLabel?: string;
  offLabel?: string;
  disabled?: boolean;
  size?: "xs" | "sm" | "md";
}

const ICONS: Record<BooleanToggleTheme, { on: LucideIcon; off: LucideIcon }> = {
  check: { on: CheckCircle2, off: XCircle },
  eye: { on: Eye, off: EyeOff },
};

export function BooleanToggle({
  value,
  onChange,
  theme = "check",
  onIcon,
  offIcon,
  onLabel,
  offLabel,
  disabled = false,
  size = "sm",
}: BooleanToggleProps) {
  const icons = ICONS[theme];
  const OnIcon = onIcon ?? icons.on;
  const OffIcon = offIcon ?? icons.off;

  const iconSize = size === "xs" ? 12 : size === "sm" ? 14 : 16;

  return (
    <SegmentedControl
      value={value ? "on" : "off"}
      onChange={(v) => onChange(v === "on")}
      disabled={disabled}
      size={size}
      data={[
        {
          value: "on",
          label: (
            <Center style={{ gap: 4 }}>
              <OnIcon size={iconSize} />
              {onLabel && <span>{onLabel}</span>}
            </Center>
          ),
        },
        {
          value: "off",
          label: (
            <Center style={{ gap: 4 }}>
              <OffIcon size={iconSize} />
              {offLabel && <span>{offLabel}</span>}
            </Center>
          ),
        },
      ]}
    />
  );
}
